// src/components/Phaser/configurator/WinConditionConfigurator.tsx

import React, { useState } from 'react';
import {
    Button, InputNumber, Select, message,
    Row, Col, Space, Input, List, Card
} from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';

// On importe le context pour manipuler la config (add, update, remove)
import { usePhaserConfig } from '../contexts/PhaserConfigContext';
import { WinCondition } from '../types/config';

const { Option } = Select;

// 1) On définit l'interface de props, comme pour CharacterConfiguratorProps, SceneConfiguratorProps, etc.
interface WinConditionConfiguratorProps {
    /** Liste complète des winConditions qu’on veut afficher/éditer **/
    winConditions: WinCondition[];
}

const WinConditionConfigurator: React.FC<WinConditionConfiguratorProps> = ({ winConditions }) => {
    // On récupère dans le contexte les méthodes qui permettent d’ajouter, mettre à jour, supprimer
    // Et aussi le reste de la config (characters, objects, texts) si nécessaire
    const {
        addWinCondition,
        updateWinCondition,
        removeWinCondition,
        characters,
        objects,
        texts,
    } = usePhaserConfig();

    // État local pour le formulaire d’édition
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [currentCondition, setCurrentCondition] = useState<WinCondition | null>(null);
    const [isModified, setIsModified] = useState<boolean>(false);

    // Sauvegarde (ajout ou update) de la win condition
    const handleSaveCondition = () => {
        if (!currentCondition) {
            message.error("Aucune condition à enregistrer.");
            return;
        }
        // Vérifications de base (selon le type de condition)
        switch (currentCondition.type) {
            case 'characterPickup':
                if (!currentCondition.targetCharacterId || !currentCondition.targetObjectId) {
                    message.warning("Sélectionnez un personnage et un objet pour 'Pickup'.");
                    return;
                }
                break;
            case 'characterAtPosition':
                if (!currentCondition.targetCharacterId) {
                    message.warning("Sélectionnez un personnage pour 'Atteindre position'.");
                    return;
                }
                break;
            case 'saySpecificPhrase':
                if (!currentCondition.targetCharacterId || !currentCondition.specificPhrase) {
                    message.warning("Sélectionnez un personnage et saisissez une phrase.");
                    return;
                }
                break;
            case 'textContentEquals':
                if (!currentCondition.targetTextObjectId || !currentCondition.expectedTextContent) {
                    message.warning("Renseignez un ID de texte et saisissez le contenu attendu.");
                    return;
                }
                break;
            default:
                break;
        }

        // Ajout ou mise à jour via le contexte
        const alreadyExists = winConditions.some((wc) => wc.id === currentCondition.id);
        if (alreadyExists) {
            updateWinCondition(currentCondition.id, currentCondition);
        } else {
            addWinCondition(currentCondition);
        }

        // Sortir du mode édition
        setIsEditing(false);
        setCurrentCondition(null);
        setIsModified(false);
    };

    // Gère la modification de n’importe quel champ
    const handleFieldChange = (field: keyof WinCondition, value: any) => {
        setCurrentCondition((prev) => (prev ? { ...prev, [field]: value } : prev));
        setIsModified(true);
    };

    // Description courte pour chaque condition dans la liste
    const getConditionDescription = (condition: WinCondition) => {
        switch (condition.type) {
            case 'characterPickup': {
                const character = characters.find(ch => ch.id === condition.targetCharacterId);
                const object = objects.find(obj => obj.id === condition.targetObjectId);
                return `Perso: ${character?.name} ramasse Obj: ${object?.name}`;
            }
            case 'characterAtPosition': {
                const character = characters.find(ch => ch.id === condition.targetCharacterId);
                return `Perso: ${character?.name} @ (${condition.targetPosition?.x}, ${condition.targetPosition?.y})`;
            }
            case 'saySpecificPhrase': {
                const character = characters.find(ch => ch.id === condition.targetCharacterId);
                return `Perso: ${character?.name} dit "${condition.specificPhrase}"`;
            }
            case 'textContentEquals': {
                const textObj = texts.find(txt => txt.id === condition.targetTextObjectId);
                const targetLabel = textObj?.textString || condition.targetTextObjectId;
                return `Texte: ${targetLabel} = "${condition.expectedTextContent}"`;            }
            default:
                return '';
        }
    };

    // Grille des conditions existantes
    const renderConditionGrid = () => (
        <div>
            <Button
                type="dashed"
                style={{ width: '100%', borderRadius: '8px', marginBottom: 16 }}
                onClick={() => {
                    const newCondition: WinCondition = {
                        id: crypto.randomUUID(),
                        type: 'characterPickup', // par défaut
                        targetCharacterId: '',
                        targetObjectId: '',
                        targetPosition: { x: 0, y: 0 },
                        specificPhrase: '',
                        targetTextObjectId: '',
                        expectedTextContent: '',
                        expectedTextBackgroundColor: '',
                    };
                    setCurrentCondition(newCondition);
                    setIsEditing(true);
                }}
            >
                <PlusOutlined /> Ajouter une WinCondition
            </Button>

            <List
                dataSource={winConditions}
                renderItem={(condition) => (
                    <List.Item key={condition.id}>
                        <Card style={{ width: '100%', position: 'relative' }}>
                            <Space style={{ position: 'absolute', top: '8px', right: '8px' }}>
                                <Button
                                    icon={<EditOutlined />}
                                    onClick={() => {
                                        setCurrentCondition(condition);
                                        setIsEditing(true);
                                    }}
                                />
                                <Button
                                    icon={<DeleteOutlined />}
                                    danger
                                    onClick={() => removeWinCondition(condition.id)}
                                />
                            </Space>
                            <Row>
                                <Col flex="auto">
                                    <strong>Type:</strong> {condition.type}
                                    <br />
                                    {getConditionDescription(condition)}
                                </Col>
                            </Row>
                        </Card>
                    </List.Item>
                )}
            />
        </div>
    );

    // Formulaire d'édition / ajout
    const renderConfigForm = () => {
        if (!currentCondition) return null;

        return (
            <div>
                <Select
                    placeholder="Type de condition"
                    style={{ width: '100%', marginBottom: 16 }}
                    value={currentCondition.type}
                    onChange={(value) => handleFieldChange('type', value)}
                >
                    <Option value="characterPickup">Pickup Perso - Objet</Option>
                    <Option value="characterAtPosition">Atteindre position</Option>
                    <Option value="saySpecificPhrase">Dire une phrase</Option>
                    <Option value="textContentEquals">Texte = Contenu spécifique</Option>
                </Select>

                {currentCondition.type === 'characterPickup' && (
                    <>
                        <Select
                            placeholder="Personnage"
                            style={{ width: '100%', marginBottom: 16 }}
                            value={currentCondition.targetCharacterId || undefined}
                            onChange={(value) => handleFieldChange('targetCharacterId', value)}
                        >
                            {characters.map((ch) => (
                                <Option key={ch.id} value={ch.id}>
                                    {ch.name}
                                </Option>
                            ))}
                        </Select>
                        <Select
                            placeholder="Objet"
                            style={{ width: '100%', marginBottom: 16 }}
                            value={currentCondition.targetObjectId || undefined}
                            onChange={(value) => handleFieldChange('targetObjectId', value)}
                        >
                            {objects.map((obj) => (
                                <Option key={obj.id} value={obj.id}>
                                    {obj.name}
                                </Option>
                            ))}
                        </Select>
                    </>
                )}

                {currentCondition.type === 'characterAtPosition' && (
                    <>
                        <Select
                            placeholder="Personnage"
                            style={{ width: '100%', marginBottom: 16 }}
                            value={currentCondition.targetCharacterId || undefined}
                            onChange={(value) => handleFieldChange('targetCharacterId', value)}
                        >
                            {characters.map((ch) => (
                                <Option key={ch.id} value={ch.id}>
                                    {ch.name}
                                </Option>
                            ))}
                        </Select>
                        <Row gutter={16} style={{ marginBottom: 16 }}>
                            <Col span={12}>
                                <InputNumber
                                    placeholder="Position X"
                                    value={currentCondition.targetPosition?.x}
                                    onChange={(val) =>
                                        handleFieldChange('targetPosition', {
                                            ...currentCondition.targetPosition,
                                            x: val,
                                        })
                                    }
                                    style={{ width: '100%' }}
                                />
                            </Col>
                            <Col span={12}>
                                <InputNumber
                                    placeholder="Position Y"
                                    value={currentCondition.targetPosition?.y}
                                    onChange={(val) =>
                                        handleFieldChange('targetPosition', {
                                            ...currentCondition.targetPosition,
                                            y: val,
                                        })
                                    }
                                    style={{ width: '100%' }}
                                />
                            </Col>
                        </Row>
                    </>
                )}

                {currentCondition.type === 'saySpecificPhrase' && (
                    <>
                        <Select
                            placeholder="Personnage"
                            style={{ width: '100%', marginBottom: 16 }}
                            value={currentCondition.targetCharacterId || undefined}
                            onChange={(value) => handleFieldChange('targetCharacterId', value)}
                        >
                            {characters.map((ch) => (
                                <Option key={ch.id} value={ch.id}>
                                    {ch.name}
                                </Option>
                            ))}
                        </Select>
                        <Input
                            placeholder="Phrase à dire"
                            style={{ width: '100%', marginBottom: 16 }}
                            value={currentCondition.specificPhrase}
                            onChange={(e) => handleFieldChange('specificPhrase', e.target.value)}
                        />
                    </>
                )}

                {currentCondition.type === 'textContentEquals' && (
                    <>
                        <Input
                            placeholder="Identifiant du texte"
                            style={{ width: '100%', marginBottom: 16 }}
                            value={currentCondition.targetTextObjectId || ''}
                            onChange={(e) => handleFieldChange('targetTextObjectId', e.target.value)}
                            list="available-text-ids"
                        />
                        <datalist id="available-text-ids">
                            {texts.map((txt) => (
                                <option key={txt.id} value={txt.id} />
                            ))}
                        </datalist>
                        <Input
                            placeholder="Contenu attendu"
                            style={{ width: '100%', marginBottom: 16 }}
                            value={currentCondition.expectedTextContent}
                            onChange={(e) => handleFieldChange('expectedTextContent', e.target.value)}
                        />
                    </>
                )}

                <div style={{ textAlign: 'center' }}>
                    <Button type="primary" onClick={handleSaveCondition}>
                        {isModified ? "Enregistrer" : "Ajouter"}
                    </Button>
                    <Button style={{ marginLeft: 8 }} onClick={() => setIsEditing(false)}>
                        Annuler
                    </Button>
                </div>
            </div>
        );
    };

    return <div>{isEditing ? renderConfigForm() : renderConditionGrid()}</div>;
};

export default WinConditionConfigurator;
